import * as React from "react";

// Utils
import {
  State,
  Action,
  BaseProviderProps,
  Dispatch,
} from "../types/BaseContext.types";

const initialState: State = {};

const BaseStateContext = React.createContext<State | undefined>(undefined);
const BaseDispatchContext = React.createContext<Dispatch | undefined>(
  undefined
);

const baseReducer = (state: State, action: Action) => {
  const { type, payload } = action;

  switch (type) {
    case "add": {
      const newState = { ...state, ...payload };
      return newState;
    }

    case "remove": {
      const newState = state;
      if (newState[payload.id]) {
        delete newState[payload.id];
      }

      return { ...newState };
    }

    default: {
      throw new Error(`Unhandled action type: ${type}`);
    }
  }
};

const BaseProvider = ({ children }: BaseProviderProps): React.ReactElement => {
  const [state, dispatch] = React.useReducer<React.Reducer<State, Action>>(
    baseReducer,
    initialState
  );

  return (
    <BaseStateContext.Provider value={state as State}>
      <BaseDispatchContext.Provider value={dispatch}>
        {children}
      </BaseDispatchContext.Provider>
    </BaseStateContext.Provider>
  );
};

const useBaseState = () => {
  const context = React.useContext(BaseStateContext);

  if (context === undefined) {
    throw new Error("useBaseState must be used within a BaseProvider");
  }

  return context;
};

const useBaseDispatch = () => {
  const context = React.useContext(BaseDispatchContext);

  if (context === undefined) {
    throw new Error("useBaseDispatch must be used within a BaseProvider");
  }

  return context;
};

export { BaseProvider, useBaseState, useBaseDispatch };
